import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllPartenaires,
  addPartenaire,
  updatePartenaire,
  deletePartenaire,
} from "../../../../../services/partenairesServices";

import {
  extractArray,
  getStats,
  isDemoMode,
  normalizeText,
} from "../utils/partenairesManagerUtils";

const CHUNK = 50;

export default function usePartenairesManager() {
  const [partenaires, setPartenaires] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [visibleCount, setVisibleCount] = useState(CHUNK);

  const [drawer, setDrawer] = useState({
    type: null,
    partenaire: null,
  });

  const demoMode = useMemo(() => isDemoMode(), []);

  const fetchPartenaires = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getAllPartenaires();

      const list = extractArray(data).sort((a, b) => {
        const ordreA = Number(a.ordre ?? 999);
        const ordreB = Number(b.ordre ?? 999);

        if (ordreA !== ordreB) return ordreA - ordreB;

        return String(a.nom || "").localeCompare(String(b.nom || ""), "fr", {
          sensitivity: "base",
        });
      });

      setPartenaires(list);
    } catch (error) {
      console.error("Erreur lors de la récupération des partenaires :", error);
      toast.error("Erreur lors de la récupération des partenaires.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPartenaires();
  }, [fetchPartenaires]);

  const stats = useMemo(() => getStats(partenaires), [partenaires]);

  const filteredPartenaires = useMemo(() => {
    const term = normalizeText(query.trim());

    return partenaires.filter((partenaire) => {
      const searchableText = [
        partenaire.nom,
        partenaire.link_url,
        partenaire.photo_url,
      ]
        .filter(Boolean)
        .join(" ");

      const matchesSearch =
        !term || normalizeText(searchableText).includes(term);

      const matchesStatus =
        !statusFilter ||
        (statusFilter === "active" && partenaire.is_active !== false) ||
        (statusFilter === "inactive" && partenaire.is_active === false);

      return matchesSearch && matchesStatus;
    });
  }, [partenaires, query, statusFilter]);

  const visiblePartenaires = useMemo(() => {
    return filteredPartenaires.slice(0, visibleCount);
  }, [filteredPartenaires, visibleCount]);

  const resetFilters = () => {
    setQuery("");
    setStatusFilter("");
    setVisibleCount(CHUNK);
  };

  const loadMore = () => {
    setVisibleCount((current) =>
      Math.min(current + CHUNK, filteredPartenaires.length)
    );
  };

  const openCreateDrawer = () => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    setDrawer({
      type: "create",
      partenaire: null,
    });
  };

  const openEditDrawer = (partenaire) => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    setDrawer({
      type: "edit",
      partenaire,
    });
  };

  const closeDrawer = () => {
    setDrawer({
      type: null,
      partenaire: null,
    });
  };

  const handleCreatePartenaire = async (payload) => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    try {
      setSaving(true);

      await addPartenaire(payload);

      toast.success("Partenaire créé avec succès.");
      await fetchPartenaires();
      closeDrawer();
    } catch (error) {
      console.error("Erreur lors de la création du partenaire :", error);
      toast.error("Erreur lors de la création du partenaire.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePartenaire = async (partenaireId, payload) => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    try {
      setSaving(true);

      await updatePartenaire({
        id: partenaireId,
        ...payload,
      });

      toast.success("Partenaire modifié avec succès.");
      await fetchPartenaires();
      closeDrawer();
    } catch (error) {
      console.error("Erreur lors de la modification du partenaire :", error);
      toast.error("Erreur lors de la modification du partenaire.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePartenaire = async (partenaire) => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    if (!partenaire?.id) return;

    const confirmed = window.confirm(
      `Supprimer le partenaire "${partenaire.nom}" ?`
    );

    if (!confirmed) return;

    try {
      await deletePartenaire(partenaire.id);

      setPartenaires((current) =>
        current.filter((item) => String(item.id) !== String(partenaire.id))
      );

      toast.success("Partenaire supprimé avec succès.");
    } catch (error) {
      console.error("Erreur lors de la suppression du partenaire :", error);
      toast.error("Erreur lors de la suppression du partenaire.");
    }
  };

  return {
    partenaires,
    loading,
    saving,
    stats,
    query,
    statusFilter,
    visiblePartenaires,
    filteredPartenaires,
    visibleCount,
    drawer,
    demoMode,

    setQuery,
    setStatusFilter,
    resetFilters,
    loadMore,
    refreshData: fetchPartenaires,

    openCreateDrawer,
    openEditDrawer,
    closeDrawer,
    handleCreatePartenaire,
    handleUpdatePartenaire,
    handleDeletePartenaire,
  };
}